import React from 'react';
import { FaGlobe, FaLocationDot } from 'react-icons/fa6';
import type { PartnerProfile } from 'ts-core';
import { UserAvatar } from '../UserAvatar';
import { SocialChips, MediaSections } from './mediaTiles';

export interface PartnerDetailPanelProps {
    profile: PartnerProfile;
    /** Optional content rendered at the bottom (e.g. a mobile close button). */
    footerSlot?: React.ReactNode;
}

/** Strip the protocol for a cleaner website display label. */
function displayWebsite(url: string): string {
    return url.replace(/^https?:\/\//i, '').replace(/\/$/, '');
}

/**
 * Read-only public partner profile pane. Mirrors ProfileDetailPanel and reuses
 * the same social chips + media tiles (./mediaTiles), with partner-specific
 * website + address rows and an admin-authored HTML "About" section.
 */
export const PartnerDetailPanel: React.FC<PartnerDetailPanelProps> = ({ profile, footerSlot }) => {
    const meta =
        typeof profile.eventsCount === 'number'
            ? `${profile.eventsCount} ${profile.eventsCount === 1 ? 'event' : 'events'}`
            : null;

    return (
        <div className="partner-detail-panel p-6 space-y-6">
            {/* Header — partner logo as avatar */}
            <header className="flex items-center gap-4">
                <UserAvatar
                    name={profile.name}
                    mainImage={profile.logo}
                    media={profile.media}
                    size={80}
                    className="ring-2 ring-gray-100 flex-shrink-0"
                />
                <div className="min-w-0">
                    <h1 className="text-2xl font-bold text-gray-900 truncate">{profile.name}</h1>
                    {meta && <p className="text-sm text-gray-500 mt-1">{meta}</p>}
                </div>
            </header>

            <SocialChips links={profile.socialLinks ?? []} />

            {/* Website + address */}
            {(profile.websiteUrl || profile.address) && (
                <div className="space-y-2 text-sm">
                    {profile.websiteUrl && (
                        <a
                            href={profile.websiteUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 text-balloon hover:underline break-all"
                        >
                            <FaGlobe className="w-4 h-4 flex-shrink-0" aria-hidden />
                            {displayWebsite(profile.websiteUrl)}
                        </a>
                    )}
                    {profile.address && (
                        <p className="flex items-start gap-2 text-gray-700">
                            <FaLocationDot className="w-4 h-4 mt-0.5 flex-shrink-0 text-gray-400" aria-hidden />
                            <span className="whitespace-pre-line">{profile.address}</span>
                        </p>
                    )}
                </div>
            )}

            {/* About — admin-authored HTML (trusted; edited in beat/Nova via Trix) */}
            {profile.htmlDescription && (
                <div
                    className="text-sm text-gray-700 leading-relaxed [&_a]:text-balloon [&_a]:underline"
                    dangerouslySetInnerHTML={{ __html: profile.htmlDescription }}
                />
            )}

            <MediaSections media={profile.media ?? []} title={profile.name || 'media'} />

            {footerSlot}
        </div>
    );
};

export default PartnerDetailPanel;
